import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logActivity } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { Service } from "@/lib/types";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  EmptyState,
  Toggle,
  ConfirmDialog,
  Field,
  Input,
  Textarea,
  Modal,
} from "@/pages/admin/components/ui";
import ImageUploader from "@/pages/admin/components/ImageUploader";

interface Draft {
  nombre: string;
  descripcion: string;
  icono: string;
  imagen: string;
  orden: number;
  activo: boolean;
}

export default function Servicios() {
  const { success, error: toastError } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Service | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>({ nombre: "", descripcion: "", icono: "ri-global-line", imagen: "", orden: 0, activo: true });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Service | null>(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from("services").select("*").order("orden");
    setServices((data ?? []) as Service[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setDraft({ nombre: "", descripcion: "", icono: "ri-global-line", imagen: "", orden: services.length + 1, activo: true });
    setOpen(true);
  }

  function openEdit(s: Service) {
    setEditing(s);
    setDraft({ nombre: s.nombre, descripcion: s.descripcion || "", icono: s.icono || "ri-global-line", imagen: s.imagen || "", orden: s.orden, activo: s.activo });
    setOpen(true);
  }

  async function save() {
    if (!draft.nombre.trim()) {
      toastError("El nombre es obligatorio.");
      return;
    }
    setSaving(true);
    const payload = { nombre: draft.nombre, descripcion: draft.descripcion || null, icono: draft.icono || null, imagen: draft.imagen || null, orden: draft.orden, activo: draft.activo };
    const { error } = editing
      ? await supabase.from("services").update(payload).eq("id", editing.id)
      : await supabase.from("services").insert(payload);
    setSaving(false);
    if (error) {
      toastError("No se pudieron guardar los cambios.");
      return;
    }
    await logActivity(editing ? "modificó un servicio" : "creó un servicio", draft.nombre);
    success("Servicio guardado correctamente.");
    setOpen(false);
    load();
  }

  async function toggle(s: Service) {
    await supabase.from("services").update({ activo: !s.activo }).eq("id", s.id);
    load();
  }

  async function move(s: Service, dir: -1 | 1) {
    const sorted = [...services].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((x) => x.id === s.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    await supabase.from("services").update({ orden: sorted[target].orden }).eq("id", s.id);
    await supabase.from("services").update({ orden: sorted[idx].orden }).eq("id", sorted[target].id);
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    const { error } = await supabase.from("services").delete().eq("id", deleting.id);
    if (error) {
      toastError("No se pudo eliminar el servicio.");
      return;
    }
    await logActivity("eliminó un servicio", deleting.nombre);
    success("Servicio eliminado.");
    setDeleting(null);
    load();
  }

  const sorted = [...services].sort((a, b) => a.orden - b.orden);

  return (
    <div>
      <PageHeader
        title="Servicios"
        subtitle={`${services.length} servicios en total`}
        actions={
          <Button onClick={openNew}>
            <i className="ri-add-line" />
            Nuevo servicio
          </Button>
        }
      />

      {loading ? (
        <Spinner label="Cargando servicios…" />
      ) : services.length === 0 ? (
        <Card>
          <EmptyState icon="ri-stack-line" title="Aún no hay servicios" action={<Button onClick={openNew}><i className="ri-add-line" />Nuevo servicio</Button>} />
        </Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((s, i) => (
            <Card key={s.id} className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <i className={`${s.icono || "ri-global-line"} text-xl`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground-900">{s.nombre}</p>
                <p className="text-xs text-foreground-500 line-clamp-1">{s.descripcion}</p>
              </div>
              <Toggle checked={s.activo} onChange={() => toggle(s)} />
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(s, -1)} disabled={i === 0} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-up-line" /></button>
                <button onClick={() => move(s, 1)} disabled={i === sorted.length - 1} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"><i className="ri-arrow-down-line" /></button>
                <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50"><i className="ri-edit-line" /></button>
                <button onClick={() => setDeleting(s)} className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"><i className="ri-delete-bin-line" /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Editar servicio" : "Nuevo servicio"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar"}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Nombre" required>
            <Input value={draft.nombre} onChange={(e) => setDraft((d) => ({ ...d, nombre: e.target.value }))} />
          </Field>
          <Field label="Descripción">
            <Textarea rows={3} value={draft.descripcion} onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Icono (Remix)" hint="Ej. ri-global-line">
              <Input value={draft.icono} onChange={(e) => setDraft((d) => ({ ...d, icono: e.target.value }))} />
            </Field>
            <Field label="Orden">
              <Input type="number" value={draft.orden} onChange={(e) => setDraft((d) => ({ ...d, orden: Number(e.target.value) }))} />
            </Field>
          </div>
          <ImageUploader label="Imagen (opcional)" value={draft.imagen} onChange={(v) => setDraft((d) => ({ ...d, imagen: v }))} folder="servicios" />
          <Toggle checked={draft.activo} onChange={(v) => setDraft((d) => ({ ...d, activo: v }))} label="Activo" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(deleting)} title="Eliminar servicio" message={`¿Seguro que quieres eliminar "${deleting?.nombre}"?`} onConfirm={confirmDelete} onCancel={() => setDeleting(null)} />
    </div>
  );
}